import type { SyncboxEmailDraftParams, SyncboxEmailThreadDetail } from "@/schema"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import { createSyncboxEmailDraft, deleteSyncboxEmailDraft, updateSyncboxEmailDraft } from "./api"

interface CreateSyncboxEmailDraftPayload {
  emailDraft: SyncboxEmailDraftParams
  templateAttachmentSignedIds?: string[]
}

interface UpdateSyncboxEmailDraftPayload extends CreateSyncboxEmailDraftPayload {
  id: string
}

const POST_SEND_INVALIDATION_DELAY_MS = 10000

export function useCreateSyncboxEmailDraft() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ emailDraft, templateAttachmentSignedIds }: CreateSyncboxEmailDraftPayload) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createSyncboxEmailDraft(context, emailDraft, templateAttachmentSignedIds)
    },
    meta: { errorToast: { fallback: "Failed to send email" } },
    onSuccess: (data, { emailDraft }) => {
      queryClient.invalidateQueries({ queryKey: ["syncbox_email_drafts"] })

      if (emailDraft.reply_to_syncbox_message_id && !emailDraft.scheduled_for) {
        queryClient.setQueriesData<SyncboxEmailThreadDetail>({ queryKey: ["syncbox_email_thread"] }, (thread) => {
          if (
            !thread ||
            !Array.isArray(thread.email_messages) ||
            !thread.email_messages.some((message) => message.id === emailDraft.reply_to_syncbox_message_id)
          ) {
            return thread
          }
          const pendingDrafts = thread.pending_drafts ?? []
          if (pendingDrafts.some((draft) => draft.id === data.id)) return thread

          return {
            ...thread,
            pending_drafts: [...pendingDrafts, data],
          }
        })
        queryClient.invalidateQueries({ queryKey: ["important_emails"] })
      }

      // Give the provider sync a moment to land the sent copy before refetching threads
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["syncbox_email_threads"] })
        queryClient.invalidateQueries({ queryKey: ["syncbox_email_thread"] })
        queryClient.invalidateQueries({ queryKey: ["syncbox_email_drafts"] })
      }, POST_SEND_INVALIDATION_DELAY_MS)
    },
  })
}

export function useUpdateSyncboxEmailDraft() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, emailDraft, templateAttachmentSignedIds }: UpdateSyncboxEmailDraftPayload) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updateSyncboxEmailDraft(context, id, emailDraft, templateAttachmentSignedIds)
    },
    meta: { errorToast: { fallback: "Failed to update scheduled email" } },
    onSuccess: (data) => {
      queryClient.setQueryData(["syncbox_email_draft", data.id], data)
      queryClient.invalidateQueries({ queryKey: ["syncbox_email_drafts"] })
    },
  })
}

export function useDeleteSyncboxEmailDraft() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return deleteSyncboxEmailDraft(context, id)
    },
    meta: { errorToast: { fallback: "Failed to cancel scheduled email" } },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["syncbox_email_drafts"] })
    },
  })
}
